import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import { Box, Chip } from '@mui/material';
import axiosInstance from '../../../../lib/axios';
import FilterTable from '../../../../atoms/TableComponent/FilterTable/FilterTable';

export default function ShiftTable() {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [tappedRowId, setTappedRowId] = useState(null);
  const [lastTap, setLastTap] = useState(0);
  const DOUBLE_TAP_DELAY = 300;

  const isMobile = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const permissions = usePermissions("Shift Master");
  const { AddRec = "0" } = permissions || {};

  useEffect(() => {
    fetchShiftData();
  }, []);

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const fetchShiftData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post("Shift/GetShiftDashboard",
        {
          SearchText: "",
        });
      const {
        data: { Status, Data },
      } = response;
      if (Status === 0 && Array.isArray(Data)) {
        const formattedData = Data.map((shift) => ({
          ...shift,
          ShiftName: shift.ShiftName || "",
          ShiftAltCode: shift.ShiftAltCode || "",
          ShiftAbrv: shift.ShiftAbrv || "",
          Remark: shift.Remark,
          Status:
            shift.Status === null
              ? ""
              : shift.Status == 1
                ? "Active"
                : "Inactive",
          // Format time fields
          FromTime: formatTime(shift.FromTime),
          ToTime: formatTime(shift.ToTime),
          TotalHours: shift.Hours || 0,
        }));
        setRows(formattedData);
      } else {
        console.error("Error fetching zone data:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching zone data:", error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleCellClicked = useCallback((params) => {
    if (!params.data?.ShiftId) return;

    const shiftId = params.data.ShiftId;
    setTappedRowId(shiftId);
    setTimeout(() => setTappedRowId(null), 300);

    const now = Date.now();
    const timeSince = now - lastTap;

    if (isMobile()) {
      navigate('/masters/shift', { state: { ShiftId: shiftId, mode: 'view' } });
      return;
    }

    if (timeSince < DOUBLE_TAP_DELAY) {
      navigate('/masters/shift', { state: { ShiftId: shiftId, mode: 'view' } });
    }
    setLastTap(now);
  }, [navigate, lastTap]);

  // Pass getRowStyle to FilterTable if it supports it
  const getRowStyle = (params) => {
    if (tappedRowId === params.data.ShiftId) {
      return { background: '#bbdefb', transition: 'background 0.2s' };
    }
    return null;
  };

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      handleClick();
    }
  };

  const handleClick = () => {
    navigate('/masters/shift', { state: { mode: "add" } });
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleLocationclick = () => {
    navigate('/masters/operations');
  };

  const handleRowDoubleClick = (params) => {
    const shiftId = params?.data?.ShiftId;
    if (shiftId) {
      navigate('/masters/shift', { state: { ShiftId: shiftId, mode: 'view' } });
    }
  };

  const TruncatedCellRenderer = (props) => {
    const [isOverflowed, setIsOverflowed] = useState(false);
    const cellRef = React.useRef(null);

    useEffect(() => {
      if (cellRef.current) {
        const isTextOverflowed =
          cellRef.current.scrollWidth > cellRef.current.clientWidth ||
          (props.value && props.value.length > 20);
        setIsOverflowed(isTextOverflowed);
      }
    }, [props.value]);

    const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

    return (
      <div
        ref={cellRef}
        className="truncated-cell"
        style={{ width: '100%' }}
      >
        {cellValue}
      </div>
    );
  };

  const StatusCellRenderer = (props) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'Active':
          return '#4caf50';
        case 'InActive':
          return '#f44336';
        default:
          return '#757575';
      }
    };

    return (
      <Chip
        label={props.value}
        className="status-chip"
        sx={{
          backgroundColor: getStatusColor(props.value),
          color: 'white',
          width: '100%',
          maxWidth: '100px',
        }}
      />
    );
  };

  const columnDefs = [
    { headerName: 'Sr.No.', field: 'RowNum', width: 60, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Shift Name', field: 'ShiftName', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Shift Abrv', field: 'ShiftAbrv', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Shift Alt Code', field: 'ShiftAltCode', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'From Time', field: 'FromTime', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'To Time', field: 'ToTime', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'TotalHours', field: 'TotalHours', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Status', field: 'Status', width: 100, filter: 'agTextColumnFilter', cellRenderer: StatusCellRenderer },
  ];

  return (
    <Box sx={{ height: '90vh' }}>
      <FilterTable
        columnDefs={columnDefs}
        rowData={rows}
        onRowDoubleClicked={handleRowDoubleClick}
        isLoading={isLoading}
        onHomeClick={handleHomeClick}
        onParentClick={handleLocationclick}
        breadcrumbParentLabel="Operations"
        breadcrumbCurrentLabel="Shift Master"
        onAddClick={handlePermittedClick}
        hasAddPermission={AddRec === "1"}
        onCellClicked={handleCellClicked}
        getRowStyle={getRowStyle}
      />
    </Box>
  );
}

